import {
  Button,
  Divider,
  Grid,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import {
  brandsToExclude,
  categoriesToExclude,
} from "../../../queries/toExclude";

import { BiChevronRight } from "../../customIcons";
import ProductCard from "../ProductCard";
import { fetchAllCategoriesWithBrands } from "../../../queries/fetchAllCategoriesWithBrands";
import { getBestSellers } from "../../../queries/getBestSellers";
import useAuth from "../../useAuth";

const CategoriesTab = () => {
  const products = fetchAllCategoriesWithBrands();
  const pathname = typeof window !== "undefined" && window.location?.pathname;
  const currentlyOnShopPage = pathname === "/shop/";

  const reloadPage = (): void => {
    if (typeof window !== "undefined") {
      const newURL = new URL(window.location.href);
      newURL.search = "";
      window.location.href = newURL.toString();
    }
  };
  const { isUnlicensedUser, loggedIn, isCustomerDefault, isBankAcc } = useAuth();
  const bestSellers = getBestSellers();
  const [selectedItem, setSelectedItem] = useState<number | undefined>(0);

  const slicedBestSellers = useMemo(() => {
    return bestSellers.slice(0, 4);
  }, [bestSellers]);

  // CATS TO EXCLUDE
  const relatedProductCategoryName = "Related Product";
  const filteredCategories = useMemo(() => {
    const categories = new Map();

    products.forEach((product: any) => {
      product.productCategories.nodes.forEach((cat: any) => {
        if (!categories.has(cat.id)) {
          categories.set(cat.id, { ...cat, productIds: new Set([product.id]) });
        } else {
          categories.get(cat.id).productIds.add(product.id);
        }
      });
    });

    const categoryArray = Array.from(categories.values());

    return categoryArray.filter((cat) => {
      const shouldInclude =
        isCustomerDefault || isUnlicensedUser || !loggedIn || isBankAcc
          ? !categoriesToExclude.includes(cat.name) &&
          cat.name !== relatedProductCategoryName &&
          cat.name !== "Best Selling Product"
          : cat.name !== "Best Selling Product" &&
          cat.name !== relatedProductCategoryName;

      return shouldInclude;
    });
  }, [
    isCustomerDefault,
    isUnlicensedUser,
    isBankAcc,
    products,
    categoriesToExclude,
    relatedProductCategoryName,
  ]);

  // Filter out categories with a parent
  const topLevelCategories = filteredCategories.filter(category => !category.wpParent);

  // Parsing categories and their brands
  const parsedCategories = useMemo(() => {
    const shouldFilterBrands =
      isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc;

    const categoriesWithDetails = topLevelCategories
      .map((category) => {
        // Fetch products in this category
        const productsInCategory = Array.from(category.productIds)
          .map((id) => products.find((product: any) => product.id === id))
          .filter((product) => product != null); // Exclude undefined products

        // Collect unique brands, excluding specific brands based on user status and globally excluded names
        const brands = new Map();
        productsInCategory.forEach((product) => {
          product.productTags.nodes.forEach((tag: any) => {
            if (
              !brands.has(tag.slug) &&
              !(shouldFilterBrands && brandsToExclude.includes(tag.name)) &&
              tag.name !== "Medical Devices" &&
              tag.name !== "Pharmaceuticals"
            ) {
              brands.set(tag.slug, tag);
            }
          });
        });

        // Convert Map to array and sort alphabetically by brand name
        const sortedBrands = Array.from(brands.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );

        // Include category only if it has valid brands
        if (sortedBrands.length > 0) {
          return {
            ...category,
            brands: sortedBrands,
          };
        }
        return null; // Return null for categories with no brands, will filter out later
      })
      .filter((category) => category != null); // Remove null categories

    // Sort categories alphabetically by their names
    return categoriesWithDetails.sort((a, b) => a.name.localeCompare(b.name));
  }, [
    topLevelCategories,
    products,
    isUnlicensedUser,
    loggedIn,
    isBankAcc,
    isCustomerDefault,
    brandsToExclude,
  ]);

  const [selectedCategory, setSelectedCategory] = useState(
    parsedCategories[0] || null,
  );

  return (
    <HStack gap={0} alignItems="flex-start" flex={1} overflow="auto">
      <HStack gap={0} justifyContent="flex-start" alignItems="flex-start">
        <VStack
          h="100vh"
          alignItems="start"
          overflowY="scroll"
          flex={1}
          w="320px"
          maxW="320px"
        >
          {currentlyOnShopPage ? (
            <Button
              variant="unstyled"
              fontSize="14px"
              color="#BB5D82"
              fontWeight="700"
              pb="20px"
              h="40px"
              onClick={reloadPage}
            >
              All Products
            </Button>
          ) : (
            <a href="https://www.medsupplysolutions.com/shop/">
              <Button
                variant="unstyled"
                fontSize="14px"
                color="#BB5D82"
                fontWeight="700"
                pb="20px"
                h="40px"
                pl="12px"
              >
                All Products
              </Button>
            </a>
          )}

          {parsedCategories?.map((item, index) => {
            const selected = index == selectedItem;
            return (
              <HStack
                cursor="pointer"
                key={index}
                w="100%"
                justifyContent="space-between"
                onClick={() => {
                  setSelectedCategory(item);
                  setSelectedItem(index);
                }}
                h="40px"
                bg={selected ? "#fff" : "#F7FAFC"}
              >
                <Text
                  pl="12px"
                  w="100%"
                  color={selected ? "#1A212D" : "#4A5568"}
                  fontWeight={selected ? "600" : "400"}
                  textDecoration={selected ? "underline" : "none"}
                >
                  {item.name}
                </Text>
                <Icon
                  width="18px"
                  height="18px"
                  mr="12px"
                  fill={selected ? "#CB809E" : "#A0AEC0"}
                  as={BiChevronRight}
                />
              </HStack>
            );
          })}
        </VStack>
        <Divider orientation="vertical" borderColor="#E2E8F0" h="100vh" />
      </HStack>
      {selectedCategory && (
        <HStack gap={0} justifyContent="flex-start" alignItems="flex-start">
          <VStack
            overflowY="auto"
            maxW={{ lg: "500px", "2xl": "564px" }}
            w={{ lg: "500px", "2xl": "564px" }}
            height="100%"
            // flex={1}
            alignItems="flex-start"
          >
            <Text pl="24px" fontSize="14px" color="#4A5568" pb="16px">
              Category Brands
            </Text>
            <a
              href={`https://www.medsupplysolutions.com/${selectedCategory.slug}/`}
            >
              <HStack alignItems="center">
                <Text
                  pl="24px"
                  fontSize="18px"
                  color="#BB5D82"
                  fontWeight="600"
                >
                  Go to {selectedCategory.name} page
                </Text>
                <Icon
                  as={BiChevronRight}
                  width="24px"
                  height="24px"
                  fill="#BB5D82"
                />
              </HStack>
            </a>
            <Divider pt="12px" w="100%" borderColor="#E2E8F0" />
            <Grid
              pt="24px"
              // width={{ lg: "330px", "2xl": "calc(100vh - 100px)" }}
              templateColumns={{
                base: "repeat(2, 1fr)",
              }}
              pl="20px"
            >
              {selectedCategory.brands?.map((item: any, index: any) => (
                <a
                  key={index}
                  href={`https://www.medsupplysolutions.com/${item.slug}/`}
                >
                  <HStack h="42px" py="auto" w={"224px"} maxW={"224px"}>
                    <Text
                      h="40px"
                      fontWeight="500"
                      _hover={{ textDecoration: "underline" }}
                      m="8px"
                      fontSize="18px"
                    >
                      {item.name}
                    </Text>{" "}
                  </HStack>
                </a>
              ))}
            </Grid>
          </VStack>
          <Divider orientation="vertical" borderColor="#E2E8F0" h="100vh" />
        </HStack>
      )}

      <VStack
        pl="12px"
        flex={1}
        maxW={{ xl: "auto", "2xl": "564px", "3xl": "100%" }}
        w={{ xl: "auto", "2xl": "564px", "3xl": "100%" }}
        display={{ base: "none", lg: "flex" }}
        alignItems="flex-start"
        mb="82px"
      >
        <Text width="100%" fontSize="14px" color="#4A5568" pb="16px">
          Best Sellers
        </Text>
        <SimpleGrid
          columns={{ md: 1, lg: 2, xl: 2, "3xl": 2, "4xl": 4 }}
          gap={2}
          h="100%"
        >
          {slicedBestSellers.map((item: any, index: any) => {
            const imageData =
              item.featuredImage?.node?.localFile?.childImageSharp
                ?.gatsbyImageData;
            const altText = item.featuredImage?.node?.altText;

            return (
              <ProductCard
                altText={altText}
                salePrice={item?.salePrice}
                border="1px solid #E1E7EF"
                heading="div"
                width="268px"
                key={index}
                imageData={imageData}
                name={item.name}
                slug={item?.slug!}
                productId={item.databaseId}
                activeSubstance={item.allPaActiveIngredient?.nodes}
                isSearch
                height="360px"
                isRelatedProducts={true}
                stockStatus={item.stockStatus}
                isProductOnPromotion={item?.isOnPromotion?.isonpromotion}
              />
            );
          })}
        </SimpleGrid>
      </VStack>
    </HStack>
  );
};

export default CategoriesTab;
